import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuth } from '@clerk/vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomePage.vue')
  },
  {
    path: '/sound-test',
    name: 'sound-test',
    component: () => import('@/views/SoundTest.vue')
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/PrivacyPolicy.vue')
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/TermsOfUse.vue')
  },
  {
    path: '/auth/sign-in',
    name: 'sign-in',
    component: () => import('@/views/auth/SignInPage.vue')
  },
  {
    path: '/auth/sign-in/factor-one',
    name: 'sign-in-factor-one',
    component: () => import('@/views/auth/SignInPage.vue')
  },
  {
    path: '/auth/sign-in/factor-two',
    name: 'sign-in-factor-two',
    component: () => import('@/views/auth/SignInPage.vue')
  },
  {
    path: '/auth/sign-up',
    name: 'sign-up',
    component: () => import('@/views/auth/SignUpPage.vue')
  },
  // Clerk path routing redirects to this URL for the verify-email step. We must render
  // the same SignUpPage with path="/auth/sign-up" so Clerk shows the verify step, not
  // a separate page with path="/auth/sign-up/verify-email-address" (which resets the flow).
  {
    path: '/auth/sign-up/verify-email-address',
    name: 'verify-email',
    component: () => import('@/views/auth/SignUpPage.vue')
  },
  {
    path: '/profile/setup',
    name: 'profile-setup',
    component: () => import('@/views/profile/ProfileSetup.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/profile/ProfileView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/edit',
    name: 'profile-edit',
    component: () => import('@/views/profile/ProfileEdit.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: () => import('@/views/LeaderboardView.vue')
  },
  {
    path: '/play',
    name: 'play',
    component: () => import('@/views/PlayMenu.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/room/:code',
    name: 'game-room',
    component: () => import('@/views/GameRoom.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/join/:code?',
    name: 'join-room',
    component: () => import('@/views/JoinRoom.vue'),
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to handle authentication
router.beforeEach(async (to, _, next) => {
  const { isSignedIn, isLoaded } = useAuth()
  
  // Wait for Clerk to load before making auth decisions
  if (!isLoaded.value) {
    return next()
  }
  
  // Prevent redirect loops between sign-in and protected routes
  if (to.path.startsWith('/auth/sign-in') && isSignedIn.value) {
    // If user is already signed in and trying to access sign-in page, redirect to play
    console.log('[Router] User is signed in and trying to access sign-in page, redirecting to play')
    return next({ path: '/play' })
  }
  
  // IMPORTANT: Do not interfere with Clerk's verification flow at all
  // Let Clerk handle all aspects of the verification process
  if (to.path.includes('/auth/sign-up') || to.path.includes('/verify-email')) {
    console.log('[Router] Allowing Clerk to handle sign-up/verification flow without interference')
    return next()
  }
  
  // Only redirect to profile setup after successful verification and sign-in
  if (to.path === '/' && isSignedIn.value) {
    console.log('[Router] User is signed in and at root path, redirecting to profile setup')
    return next({ path: '/profile/setup' })
  }
  
  // Check if the route requires authentication
  if (to.meta.requiresAuth) {    
    if (!isSignedIn.value) {
      // Redirect to sign-in page if not authenticated
      console.log('[Router] Route requires auth but user is not signed in, redirecting to sign-in')
      return next({ path: '/auth/sign-in' })
    }
  }
  
  // Continue navigation for all other cases
  next()
})

export default router
